import api from '../../../services/api';
import {
  setListings,
  setLoading,
  setError,
  setPagination,
  setFilters,
  clearFilters,
  setExpandedRowKeys
} from '../reducers';
import { FILTER_TYPES } from '../constants/filterTypes';

// Hàm lấy danh sách listings từ API với phân trang
export const fetchListings = (page = 1, pageSize = 25) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    // Lấy thông tin filter hiện tại
    const { filters } = getState().listings;

    // Tạo query params cho API request
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('pageSize', pageSize);

    // Thêm các filter vào query params nếu có
    if (filters.searchText) {
      queryParams.append('searchText', filters.searchText);
    }
    if (filters.searchScope && filters.searchScope !== 'all') {
      queryParams.append('searchScope', filters.searchScope);
    }

    if (filters.selectedFulfillmentChannel && filters.selectedFulfillmentChannel !== 'all') {
      queryParams.append('fulfillmentChannel', filters.selectedFulfillmentChannel);
    }

    if (filters.selectedStatus && filters.selectedStatus !== 'all') {
      queryParams.append('status', filters.selectedStatus);
    }

    if (filters.selectedSellerAccount) {
      if (Array.isArray(filters.selectedSellerAccount) && filters.selectedSellerAccount.length > 0) {
        // Handle multiple seller accounts
        filters.selectedSellerAccount.forEach(accountId => {
          queryParams.append('sellerAccountId', accountId);
        });
      } else if (filters.selectedSellerAccount !== 'all') {
        queryParams.append('sellerAccountId', filters.selectedSellerAccount);
      }
    }

    if (filters.compoundFilters && filters.compoundFilters.length > 0) {
      queryParams.append('compoundFilters', JSON.stringify(filters.compoundFilters));
    }
    if (filters.salesPeriod) queryParams.append('salesPeriod', filters.salesPeriod);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
    if (filters.sortDays) queryParams.append('sortDays', filters.sortDays);

    const response = await api.get(`/inventory/listings?${queryParams.toString()}`);

    dispatch(setExpandedRowKeys([]));
    dispatch(setListings(response.data.rows));
    dispatch(setPagination({
      current: response.data.pagination.page,
      pageSize: response.data.pagination.pageSize,
      total: response.data.pagination.total
    }));
    
  } catch (error) {
    console.error('Error fetching listings:', error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

// Hàm lấy children của một listing
export const fetchListingChildren = (parentSku) => async (dispatch) => {
  try {
    const response = await api.get(`/inventory/listings/${parentSku}/children`);
    return response.data;
  } catch (error) {
    console.error('Error fetching listing children:', error);
    return [];
  }
};

export const fetchGroupedSkus = (parentSku, options = {}) => async () => {
  try {
    const queryParams = new URLSearchParams();
    if (options.groupSortBy) queryParams.append('groupSortBy', options.groupSortBy);
    if (options.groupSortOrder) queryParams.append('groupSortOrder', options.groupSortOrder);
    if (options.salesPeriod || options.groupSortDays) queryParams.append('days', options.salesPeriod || options.groupSortDays);
    const query = queryParams.toString();
    const response = await api.get(`/inventory/listings/${encodeURIComponent(parentSku)}/grouped-skus${query ? `?${query}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching grouped SKUs:', error);
    return [];
  }
};

export const fetchGroupedSkuChildren = (parentSku, groupedSku, options = {}) => async () => {
  try {
    const queryParams = new URLSearchParams();
    if (options.salesPeriod || options.groupSortDays) queryParams.append('days', options.salesPeriod || options.groupSortDays);
    const query = queryParams.toString();
    const response = await api.get(
      `/inventory/listings/${encodeURIComponent(parentSku)}/grouped-skus/${encodeURIComponent(groupedSku)}/children${query ? `?${query}` : ''}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching grouped SKU children:', error);
    return [];
  }
};

export const fetchListingChart = ({ parentSku, level = 'parent', groupedSku, sku, days = 30 }) => async () => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('level', level);
    queryParams.append('days', days);
    if (groupedSku) queryParams.append('groupedSku', groupedSku);
    if (sku) queryParams.append('sku', sku);

    const response = await api.get(`/inventory/listings/${encodeURIComponent(parentSku)}/chart?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching listing chart:', error);
    return null;
  }
};

export const fetchBestSalesPeriods = (payload) => async () => {
  try {
    const response = await api.post('/inventory/listings/best-period', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching best sales periods:', error);
    return { rows: [] };
  }
};

export const previewBulkDeleteListings = (payload) => async () => {
  const response = await api.post('/inventory/listings/bulk-delete/preview', payload);
  return response.data;
};

export const bulkDeleteListings = (payload) => async () => {
  const response = await api.delete('/inventory/listings/bulk-delete', { data: payload });
  return response.data;
};

// Generic filter change handler
export const handleFilterChange = (filterType, value) => (dispatch) => {
  const filterUpdate = {};
  filterUpdate[filterType] = value;
  dispatch(setFilters(filterUpdate));
};

// Individual filter change handlers for backward compatibility
export const handleSearchChange = (searchText) => (dispatch) => {
  dispatch(setFilters({ [FILTER_TYPES.SEARCH]: searchText }));
};

export const handleFulfillmentChannelChange = (selectedFulfillmentChannel) => (dispatch) => {
  dispatch(setFilters({ [FILTER_TYPES.FULFILLMENT_CHANNEL]: selectedFulfillmentChannel }));
};

export const handleStatusChange = (selectedStatus) => (dispatch) => {
  dispatch(setFilters({ [FILTER_TYPES.STATUS]: selectedStatus }));
};

export const handleSellerAccountChange = (selectedSellerAccount) => (dispatch) => {
  dispatch(setFilters({ [FILTER_TYPES.SELLER_ACCOUNT]: selectedSellerAccount }));
};

export const handleClearFilters = () => (dispatch) => {
  dispatch(clearFilters());
};

export const applyFilters = () => (dispatch, getState) => {
  const { pagination } = getState().listings;
  dispatch(setPagination({ current: 1, pageSize: pagination.pageSize }));
  dispatch(fetchListings(1, pagination.pageSize)); // Reset to first page
};

export const handlePaginationChange = (page, pageSize) => (dispatch) => {
  dispatch(setPagination({ current: page, pageSize }));
  dispatch(fetchListings(page, pageSize));
};

export const handleExpandedRowsChange = (expandedRowKeys) => (dispatch) => {
  dispatch(setExpandedRowKeys(expandedRowKeys));
};
